import type { EChartsOption, BarSeriesOption } from 'echarts'
import type { DefaultTheme } from 'styled-components'
import type {
  HistogramChartProps,
  HistogramSeries,
} from '../DistributionSection.types'

export const buildHistogramOption = (
  {
    title,
    data,
    unit,
    accentColor,
    series,
    listingsLabel,
  }: HistogramChartProps,
  theme: DefaultTheme
): EChartsOption => {
  const grouped = Boolean(series?.length)
  const chartSeries: HistogramSeries[] = series?.length
    ? series
    : [
        {
          name: title,
          data,
          color: accentColor || theme.colors.primary,
          statsLabel: '',
        },
      ]
  // Both categories use the same ordered buckets, including zeroes for missing ranges.
  const buckets = [
    ...new Map(
      chartSeries.flatMap((item) => item.data).map((item) => [item.label, item])
    ).values(),
  ].sort((a, b) => a.value - b.value)
  const rotate =
    buckets.length > 5 || buckets.some((item) => item.label.length > 6)

  return {
    backgroundColor: 'transparent',
    title: {
      text: title,
      left: 'center',
      top: 8,
      textStyle: {
        fontSize: 14,
        fontWeight: 600,
        color: theme.colors.text.primary,
      },
    },
    legend: grouped
      ? {
          data: chartSeries.map((item) => item.name),
          top: 34,
          textStyle: { color: theme.colors.text.secondary, fontSize: 11 },
        }
      : { show: false },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      renderMode: 'richText',
      backgroundColor: theme.colors.background.primary,
      borderColor: theme.colors.border.default,
      textStyle: { color: theme.colors.text.primary },
      formatter: (params) => {
        const points = Array.isArray(params) ? params : [params]
        const label = points[0]?.name
        if (label === undefined) return ''
        const lines = chartSeries.map((item) => {
          const bucket = item.data.find((entry) => entry.label === label)
          const count = bucket?.count || 0
          const percentage = bucket?.percentage || 0
          const name = grouped ? `${item.name}: ` : ''
          return `${name}${count.toLocaleString()} ${listingsLabel || ''} (${percentage.toFixed(1)}%)`
        })
        return [`${label} ${unit}`, ...lines].join('\n')
      },
    },
    grid: {
      left: 40,
      right: 16,
      top: grouped ? 72 : 60,
      bottom: rotate ? 60 : 40,
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      data: buckets.map((item) => item.label),
      axisLine: { lineStyle: { color: theme.colors.border.default } },
      axisLabel: {
        fontSize: 10,
        color: theme.colors.text.secondary,
        rotate: rotate ? 45 : 0,
        interval: 0,
      },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value',
      minInterval: 1,
      axisLine: { show: false },
      axisLabel: {
        fontSize: 10,
        color: theme.colors.text.secondary,
        formatter: (value: number) =>
          value >= 1000 ? `${(value / 1000).toFixed(0)}k` : `${value}`,
      },
      splitLine: {
        lineStyle: { color: theme.colors.border.light, type: 'dashed' },
      },
    },
    series: chartSeries.map(
      (item): BarSeriesOption => ({
        name: item.name,
        type: 'bar',
        barMaxWidth: 40,
        data: buckets.map(
          (bucket) =>
            item.data.find((entry) => entry.label === bucket.label)?.count || 0
        ),
        itemStyle: { color: item.color, borderRadius: [4, 4, 0, 0] },
        emphasis: { itemStyle: { opacity: 0.8 } },
        label: {
          show: buckets.length <= 10,
          position: 'top',
          fontSize: 9,
          color: theme.colors.text.secondary,
          formatter: (params) => {
            const bucket = item.data.find(
              (entry) => entry.label === params.name
            )
            return bucket?.count ? `${bucket.percentage.toFixed(0)}%` : ''
          },
        },
      })
    ),
  }
}
